/**
 * The intervenant's photo before their name wherever it is a label (Pascal,
 * 30/09/2026 : « Peux-tu systématiquement ajouter la photo de l'intervenant
 * dans tous les endroits où il est mentionné, tout comme le logo de la
 * société est toujours juxtaposé à son nom ? »). Label = table cell or row
 * header, chip, list, card, panel title, a picker's displayed value, a
 * calendar event label. Never inside a running sentence, a tooltip, an
 * aria-label, a toast, an <option> or a placeholder (EXIGENCES.md §11
 * point 14).
 *
 * The person is resolved from the sous-traitants the app already loads: the
 * page's own lookups when it has them (IntervenantDirectoryProvider), else
 * the `?tab=soustraitants` list through React Query, shared with the
 * Intervenants page (same key, deduplicated and cached). The photo is always
 * the same-origin `/api/intervenants/{id}/photo` route — the raw « Photo
 * URL » is only a version marker, never an <img> source. Nothing resolved,
 * no photo, or an image that fails: the initials on a stable hue, never a
 * broken image. Claire (an empty executor, « Claire », « Claire (interne) »)
 * is Claire's own record, so her photo shows.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import { Avatar } from '../ui/Avatar';
import { buildApiUrl, fetchTabData, intervenantPhotoUrl } from '../../lib/api';
import { useUiStore } from '../../store/ui';

export type IntervenantAvatarSize = 'sm' | 'md';

/** sm = 18 px (dense tables, plan rows, chips), md = 24 px (lists, panels, cards). */
export const INTERVENANT_AVATAR_PX: Record<IntervenantAvatarSize, number> = { sm: 18, md: 24 };

/** Id of the internal Claire line in the Planification load summaries. */
const CLAIRE_INTERNAL_ID = 'claire_internal';

export interface IntervenantMatch {
  id: string;
  name: string;
  /** Same-origin photo route, absent when the intervenant has no photo. */
  photoUrl?: string;
}

export interface IntervenantRef {
  id?: string | null;
  name?: string | null;
}

export interface IntervenantDirectory {
  size: number;
  resolve: (ref: IntervenantRef) => IntervenantMatch | null;
}

interface DirectoryEntry {
  id: string;
  name: string;
  key: string;
  tokens: string[];
  photoUrl?: string;
}

function normalizeName(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    // A compound first name stays one word: « Marie-Claire » is not « Claire ».
    .replace(/(?<=[a-z0-9])-(?=[a-z0-9])/g, '_')
    .replace(/[^a-z0-9_]+/g, ' ')
    .trim();
}

function text(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
}

/** Photo routes only: `[API base]/api/intervenants/{id}/photo?v=…`, nothing else. */
const PHOTO_ROUTE = /^\/api\/intervenants\/[^/?#\s]+\/photo\?v=[^#\s]*$/;

function photoRoute(id: string, ref: unknown): string | undefined {
  const url = intervenantPhotoUrl(id, text(ref));
  if (!url) return undefined;
  const base = buildApiUrl('');
  const path = base && url.startsWith(base) ? url.slice(base.length) : url;
  // Built from the id and a version marker, so always this route; anything
  // else (a remote address, another route) never becomes an image source.
  return PHOTO_ROUTE.test(path) ? url : undefined;
}

const EMPTY_DIRECTORY: IntervenantDirectory = { size: 0, resolve: () => null };
const directoryCache = new WeakMap<readonly unknown[], IntervenantDirectory>();

/**
 * Index of the sous-traitants (`{id | fibery/id, name, photo_url}`): by id,
 * by exact name, by the words of a partial name when only one person has
 * them, and Claire.
 */
export function buildIntervenantDirectory(records: readonly unknown[] | null | undefined): IntervenantDirectory {
  if (!Array.isArray(records) || !records.length) return EMPTY_DIRECTORY;
  const cached = directoryCache.get(records);
  if (cached) return cached;
  const entries: DirectoryEntry[] = [];
  const byId = new Map<string, DirectoryEntry>();
  records.forEach((record) => {
    if (!record || typeof record !== 'object') return;
    const row = record as Record<string, unknown>;
    const id = text(row['fibery/id']) || text(row.id);
    const name = text(row.name);
    if (!id || !name || byId.has(id)) return;
    const key = normalizeName(name);
    const entry: DirectoryEntry = { id, name, key, tokens: key.split(' ').filter(Boolean), photoUrl: photoRoute(id, row.photo_url) };
    entries.push(entry);
    byId.set(id, entry);
  });
  const byName = new Map<string, DirectoryEntry | null>();
  entries.forEach((entry) => {
    // Two people with the same name: the name alone designates nobody.
    byName.set(entry.key, byName.has(entry.key) ? null : entry);
  });
  // Claire only when there is no doubt: the record named exactly « Claire »,
  // else the single record with the word « Claire ». Two candidates: nobody
  // (initials), never another person's photo on her name.
  const exactClaire = entries.filter((entry) => entry.key === 'claire');
  const wordClaire = entries.filter((entry) => entry.tokens.includes('claire'));
  const claire = exactClaire.length === 1
    ? exactClaire[0]
    : exactClaire.length === 0 && wordClaire.length === 1 ? wordClaire[0] : null;
  const toMatch = (entry: DirectoryEntry | null | undefined): IntervenantMatch | null => (
    entry ? { id: entry.id, name: entry.name, photoUrl: entry.photoUrl } : null
  );
  const directory: IntervenantDirectory = {
    size: entries.length,
    resolve: ({ id, name }) => {
      const personId = text(id);
      const byIdMatch = personId ? byId.get(personId) : undefined;
      if (byIdMatch) return toMatch(byIdMatch);
      const key = normalizeName(name);
      if (key) {
        const exact = byName.get(key);
        if (exact) return toMatch(exact);
      }
      // Claire: an empty executor, her internal line, « Claire (interne) ».
      const tokens = key.split(' ').filter(Boolean);
      const claireRef = personId === CLAIRE_INTERNAL_ID || (!personId && !key) || tokens.includes('claire');
      if (claireRef && claire && tokens.every((token) => token === 'interne' || claire.tokens.includes(token))) {
        return toMatch(claire);
      }
      if (!tokens.length) return null;
      // A partial name (« Sophie », « S. Garcelon ») only when one person fits.
      const candidates = entries.filter((entry) => tokens.every((token) => entry.tokens.includes(token)));
      return candidates.length === 1 ? toMatch(candidates[0]) : null;
    },
  };
  directoryCache.set(records, directory);
  return directory;
}

const DirectoryContext = createContext<readonly unknown[] | null>(null);
const NO_PEOPLE: readonly unknown[] = Object.freeze([]);

/**
 * Gives the page's own sous-traitant lookups to every intervenant name below
 * it (Planification, Calendrier, Missions): no extra request. `people` still
 * loading = initials until it arrives.
 */
export function IntervenantDirectoryProvider({ people, children }: { people: readonly unknown[] | null | undefined; children: ReactNode }) {
  return <DirectoryContext.Provider value={Array.isArray(people) ? people : NO_PEOPLE}>{children}</DirectoryContext.Provider>;
}

// Components rendered without a QueryClientProvider (isolated tests) read an
// idle client: no request, the initials.
const IDLE_QUERY_CLIENT = new QueryClient({ defaultOptions: { queries: { enabled: false, retry: false } } });

/**
 * Resolves a person (id, or name when only a name is known) to `{ id, name,
 * photoUrl }`. Source: the provider's lookups, else the `soustraitants` tab
 * (React Query, the Intervenants page's key: fetched once, cached).
 */
export function useIntervenantDirectory(): IntervenantDirectory {
  const seeded = useContext(DirectoryContext);
  const queryClient = useContext(QueryClientContext);
  const year = useUiStore((state) => state.selectedYear);
  const query = useQuery(
    {
      queryKey: ['tab', 'soustraitants', year],
      queryFn: () => fetchTabData('soustraitants', year),
      enabled: seeded === null && Boolean(queryClient),
      staleTime: 5 * 60_000,
    },
    queryClient ?? IDLE_QUERY_CLIENT,
  );
  const fetched = query.data?.entities;
  const records = seeded ?? (Array.isArray(fetched) ? fetched : NO_PEOPLE);
  return useMemo(() => buildIntervenantDirectory(records), [records]);
}

/** The resolved person of a mention, or null (initials of the name shown). */
export function useIntervenant(ref: IntervenantRef): IntervenantMatch | null {
  const directory = useIntervenantDirectory();
  const id = text(ref.id);
  const name = text(ref.name);
  return useMemo(() => directory.resolve({ id, name }), [directory, id, name]);
}

/**
 * The avatar alone, to put before a label that stays one text node (a chip,
 * a button, a calendar event line). Decorative: hidden from assistive tech,
 * the visible name carries the meaning. Without a name, nothing.
 */
export function IntervenantAvatar({
  id,
  name,
  size = 'sm',
  className = '',
}: {
  id?: string | null;
  name: string | null | undefined;
  size?: IntervenantAvatarSize;
  className?: string;
}) {
  const label = text(name);
  const match = useIntervenant({ id, name: label });
  if (!label) return null;
  return (
    <Avatar
      // The directory's full name, so a person keeps the same initials and hue
      // on every page (« Claire » and « Claire Mauzé » alike).
      name={match?.name || label}
      size={INTERVENANT_AVATAR_PX[size]}
      photoUrl={match?.photoUrl}
      className={`c-person__avatar c-person__avatar--${size}${className ? ` ${className}` : ''}`}
    />
  );
}

/**
 * An intervenant's name preceded by their photo (or initials). The name stays
 * ONE text node (`getByText('Claire')` finds it) and keeps its ellipsis in
 * dense rows. Without a name nothing is drawn.
 */
export function IntervenantName({
  id,
  name,
  size = 'sm',
  className = '',
  textClassName = '',
  title,
}: {
  id?: string | null;
  name: string | null | undefined;
  size?: IntervenantAvatarSize;
  className?: string;
  textClassName?: string;
  title?: string;
}) {
  const label = text(name);
  if (!label) return null;
  return (
    <span className={`c-person c-person--${size}${className ? ` ${className}` : ''}`} title={title}>
      <IntervenantAvatar id={id} name={label} size={size} />
      <span className={`c-person__name${textClassName ? ` ${textClassName}` : ''}`}>{label}</span>
    </span>
  );
}
